<template>
  <div
    class="kf-otp"
    :class="{ 'kf-otp--error': error, 'kf-otp--off': disabled }"
    role="group"
    :aria-label="ariaLabel"
  >
    <input
      v-for="i in length"
      :key="i"
      ref="cells"
      class="kf-otp__cell"
      :class="{ 'kf-otp__cell--filled': digits[i - 1] }"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      maxlength="1"
      :disabled="disabled"
      :value="digits[i - 1]"
      :aria-label="`${ariaLabel} digit ${i}`"
      @input="onInput(i - 1, $event)"
      @keydown="onKeydown(i - 1, $event)"
      @paste="onPaste"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
/**
 * A boxed, one-digit-per-cell code entry.
 *
 * The panel had no such component -- every OTP in the product was a single bare
 * text line, which gives the person typing no idea how long the code is or how
 * far through it they are. This is the standard alternative: `length` cells that
 * fill left to right.
 *
 * The prop is the source of truth, not the cells. Each cell renders a character
 * of `value`, and every edit re-emits the whole string -- so the parent can
 * clear the field (a failed verify, a resend) by assigning "" and the cells
 * follow. `syncDom` exists for the one case that breaks: when a keystroke is
 * rejected (a letter) or replaced, the browser has already written it into the
 * input, and Vue will not repaint an element whose bound value did not change.
 */
export default {
  name: "KfOtpInput",
  props: {
    value: { type: String, default: "" },
    length: { type: Number, default: 5 },
    error: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "OTP" },
  },
  computed: {
    digits() {
      const chars = String(this.value || "").split("");
      return Array.from({ length: this.length }, (_, i) => chars[i] || "");
    },
  },
  methods: {
    /* Focus a cell by index, clamped. Also used by the parent via $refs. */
    focus(index = 0) {
      const cells = this.$refs.cells || [];
      const target = cells[Math.max(0, Math.min(index, cells.length - 1))];
      if (target) target.focus();
    },
    /* Push the model back into the DOM after a rejected or replaced keystroke. */
    syncDom() {
      (this.$refs.cells || []).forEach((cell, i) => {
        const want = this.digits[i] || "";
        if (cell.value !== want) cell.value = want;
      });
    },
    emit(next) {
      const code = next.join("");
      this.$emit("input", code);
      if (code.length === this.length) this.$emit("complete", code);
      this.$nextTick(this.syncDom);
    },
    setDigit(index, char) {
      const next = this.digits.slice();
      next[index] = char;
      this.emit(next);
    },
    /* A run of digits landing in one cell -- a paste the browser routed to the
       input, or an SMS autofill. Spread it across the cells from here on. */
    fillFrom(index, raw) {
      const next = this.digits.slice();
      const chars = raw.split("");
      let i = index;
      while (i < this.length && chars.length) next[i++] = chars.shift();
      this.emit(next);
      this.focus(i);
    },
    onInput(index, event) {
      const raw = event.target.value.replace(/\D/g, "");
      if (!raw) {
        this.setDigit(index, "");
        return;
      }
      if (raw.length > 1) {
        this.fillFrom(index, raw);
        return;
      }
      this.setDigit(index, raw);
      this.focus(index + 1);
    },
    onKeydown(index, event) {
      const key = event.key;
      if (key === "Backspace") {
        event.preventDefault();
        if (this.digits[index]) {
          // Clear where you are.
          this.setDigit(index, "");
        } else {
          // Already empty -- step back and clear that one, which is what the
          // key is being pressed for.
          this.setDigit(index - 1 < 0 ? 0 : index - 1, "");
          this.focus(index - 1);
        }
        return;
      }
      if (key === "Delete") {
        event.preventDefault();
        this.setDigit(index, "");
        return;
      }
      if (key === "ArrowLeft") {
        event.preventDefault();
        this.focus(index - 1);
        return;
      }
      if (key === "ArrowRight") {
        event.preventDefault();
        this.focus(index + 1);
        return;
      }
      // Enter is deliberately not handled: it bubbles to the surrounding form,
      // which is what makes the code submittable from the keyboard.
    },
    onPaste(event) {
      const text = (event.clipboardData || window.clipboardData).getData("text");
      const raw = String(text || "").replace(/\D/g, "");
      if (!raw) return;
      event.preventDefault();
      this.fillFrom(0, raw.slice(0, this.length));
    },
  },
};
</script>

<style scoped>
.kf-otp {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.kf-otp__cell {
  width: 52px;
  height: 56px;
  flex: 0 0 auto;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: var(--kf-text);
  background: var(--kf-surface-2);
  border: 1px solid var(--kf-border);
  border-radius: var(--kf-radius);
  outline: none;
  caret-color: var(--kf-brand);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
/* A cell that already holds a digit carries a firmer edge, so the eye can see
   how far along the code is without reading the digits. */
.kf-otp__cell--filled {
  border-color: var(--kf-border-strong);
}
.kf-otp__cell:focus {
  border-color: var(--kf-brand);
  box-shadow: 0 0 0 3px rgba(25, 78, 118, 0.12);
}
.kf-otp--error .kf-otp__cell {
  border-color: var(--kf-danger);
}
.kf-otp--error .kf-otp__cell:focus {
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}
.kf-otp--off .kf-otp__cell {
  opacity: 0.55;
}

@media (max-width: 600px) {
  .kf-otp {
    gap: 8px;
  }
  .kf-otp__cell {
    width: 46px;
    height: 52px;
    font-size: 20px;
  }
}
</style>
